import { Formik } from "formik";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { TypeableDatePicker } from "@volca/ui/components/ui/typeable-date-picker";
import { QUICKBOOKS_DATE_FORMAT } from "@/business/quickbooks-utils";
import { dayjs } from "@/core/utils/date";

const HELP_ID = "quickbooks-bills-start-date-help";

/**
 * The bill start date as `YYYY-MM-DD`, or null when cleared. The typeable
 * picker reads its validation state from Formik, so it gets a local one, as in
 * the onboarding period-start picker.
 */
export const QuickbooksStartDateField = ({
  value,
  onChange,
  disabled,
}: {
  value: string | null;
  onChange: (value: string | null) => void;
  disabled?: boolean;
}) => {
  const parsed = value ? dayjs(value, QUICKBOOKS_DATE_FORMAT, true) : null;
  const dateValue = parsed?.isValid() ? parsed.toDate() : null;

  return (
    <Stack className="w-full" align="start" gap={2}>
      <Formik
        enableReinitialize
        initialValues={{ billsStartDate: dateValue }}
        onSubmit={() => undefined}
      >
        {({ setFieldValue, values }) => (
          <TypeableDatePicker
            name="billsStartDate"
            label="Start date"
            // The picker's visible label is decoration, not a <label>, so
            // the input is named and described here.
            aria-label="Start date"
            aria-describedby={HELP_ID}
            value={values.billsStartDate}
            disabled={disabled}
            onChange={(date) => {
              void setFieldValue("billsStartDate", date);
              onChange(
                date ? dayjs(date).format(QUICKBOOKS_DATE_FORMAT) : null,
              );
            }}
            fullWidth
          />
        )}
      </Formik>
      <Text id={HELP_ID} variant="caption" tone="muted">
        Bills are only created for invoices dated on or after this date. Turn
        off AP bill export in ServiceTitan as of the same date so bills aren’t
        created twice.
      </Text>
    </Stack>
  );
};
